import { useCallback, useEffect, useRef } from "react";
import { App as CapacitorApp } from "@capacitor/app";
import { useNavigate } from "react-router-dom";
import { HOME } from "@/routes";
import { consumePendingDeepLink } from "@/utils/push-notifications";
import { isNativeApp } from "@/platform";

const PUBLIC_ONLY_PATHS = ["/login", "/signup", "/getting-started", "/landing"];

function normalizeLocalDeepLink(rawLink: string): string | null {
  const trimmed = rawLink.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith("/")) {
    if (trimmed.startsWith("/entity/")) {
      return `/stuff/${trimmed.slice("/entity/".length)}`;
    }
    return trimmed;
  }

  if (!trimmed.includes("://")) {
    return `/${trimmed}`;
  }

  try {
    const url = new URL(trimmed);
    if (url.pathname.startsWith("/entity/")) {
      return `/stuff/${url.pathname.slice("/entity/".length)}${url.search}${url.hash}`;
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return trimmed;
  }
}

interface DeepLinksOptions {
  isAuthenticated: boolean;
  isLoading: boolean;
}

export function useDeepLinks({ isAuthenticated, isLoading }: DeepLinksOptions) {
  const navigate = useNavigate();
  const pendingNativeDeepLink = useRef<string | null>(null);

  const processNativeDeepLink = useCallback(
    (url: string) => {
      try {
        const urlObj = new URL(url);
        const pathname = urlObj.pathname;

        if (pathname.startsWith("/reset-password/")) {
          const token = pathname.replace("/reset-password/", "").replace(/\/$/, "").trim();
          if (token) {
            navigate(`/login?token=${token}`);
          }
          return;
        }

        if (isAuthenticated && PUBLIC_ONLY_PATHS.some(path => pathname.startsWith(path))) {
          console.debug("[DeepLink] Authenticated user deep-linked to public route, redirecting to /home:", pathname);
          navigate(HOME());
          return;
        }

        navigate(pathname + urlObj.search);
      } catch (error) {
        console.error("Failed to parse deep link URL:", error);
      }
    },
    [navigate, isAuthenticated],
  );

  const handleLocalDeepLink = useCallback(
    (rawLink: string) => {
      const normalized = normalizeLocalDeepLink(rawLink);
      if (!normalized) return;

      if (normalized.startsWith("/")) {
        navigate(normalized);
        return;
      }

      window.location.href = normalized;
    },
    [navigate],
  );

  useEffect(() => {
    if (!isNativeApp()) return;

    const handleAppUrlOpen = (event: { url: string }) => {
      if (isLoading) {
        console.debug("[DeepLink] Queuing deep link until auth hydration completes:", event.url);
        pendingNativeDeepLink.current = event.url;
        return;
      }

      processNativeDeepLink(event.url);
    };

    const listener = CapacitorApp.addListener("appUrlOpen", handleAppUrlOpen);

    return () => {
      void listener.then(subscription => subscription.remove());
    };
  }, [isLoading, processNativeDeepLink]);

  useEffect(() => {
    if (isLoading || !pendingNativeDeepLink.current) return;

    console.debug("[DeepLink] Auth hydrated, processing queued deep link:", pendingNativeDeepLink.current);
    processNativeDeepLink(pendingNativeDeepLink.current);
    pendingNativeDeepLink.current = null;
  }, [isLoading, processNativeDeepLink]);

  useEffect(() => {
    if (!isLoading) {
      const pending = consumePendingDeepLink();
      if (pending) {
        handleLocalDeepLink(pending);
      }
    }

    const listener = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (typeof detail !== "string") return;
      if (isLoading) return;

      handleLocalDeepLink(detail);
      consumePendingDeepLink();
    };

    window.addEventListener("stasht:deep-link", listener);
    return () => {
      window.removeEventListener("stasht:deep-link", listener);
    };
  }, [handleLocalDeepLink, isLoading]);
}
